import { formatDeliveryDate } from "../../../../utils/deliveryDateFormat";
import { deliveryAddressLabel } from "../../../SaleOrder/detail/DeliveryTrip/deliveryAllocations";
import { useContext, useMemo, useState } from "react";
import { GroupedDeliveryTrip } from "../../../../data/entities";
import { Checkbox, TableRow, Tooltip } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import LoadingButton from "../../../../shared/components/LoadingButton";
import apiMethods from "../../../../utils/api";
import { error, success } from "../../../../utils/toast";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import Status from "../../../../shared/components/Status";
import dayjs from "dayjs";
import { deliveryTripBarChartColors } from "../../../../configs/deliveryTrip";
import { TooltipCategory, TooltipCircle, TooltipContent, TooltipDot } from "./Styled";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { DELIVERY_TRIP_STATUS, ROLE } from "../../../../data/enums";

export interface DeliveryTripRowProps {
    item: GroupedDeliveryTrip;
    handleRefresh: () => void;
    extraRowProps: {
        onSelect: (item: any) => void;
        isSelected: boolean;
    };
}

export const DeliveryTripRow = ({ item, handleRefresh, extraRowProps }: DeliveryTripRowProps) => {
    const [updatingTripId, setUpdatingTripId] = useState<string | null>(null);
    const normalizeStatus = (status?: string) => (status || "").toUpperCase();
	const { user: { role: userRole } } = useContext(AuthenticationContext);

	const canComplete = useMemo(() => (
			[ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(userRole) === false
		),
		[userRole],
	);

    const getStatusColorTheme = (status: string) => {
        switch (normalizeStatus(status)) {
            case DELIVERY_TRIP_STATUS.PENDING:
                return "INFO";  // Blue for "Chờ xử lý"
            case DELIVERY_TRIP_STATUS.DELIVERED:
                return "SUCCESS";
            case DELIVERY_TRIP_STATUS.IN_TRANSIT:
                return "PENDING";  // Orange for "Đang vận chuyển"
            default:
                return "INFO";
        }
    };

    const getStatusText = (status: string) => {
        switch (normalizeStatus(status)) {
            case DELIVERY_TRIP_STATUS.PENDING:
                return "Chờ xử lý";
            case DELIVERY_TRIP_STATUS.DELIVERED:
                return "Đã vận chuyển";
            case DELIVERY_TRIP_STATUS.IN_TRANSIT:
                return "Đang vận chuyển";
            default:
                return "";
        }
    };

    const formatDate = formatDeliveryDate;

    const isTripReadyForCompletion = (trip: any) => {
        const items = trip.delivery_products || trip.items || [];
        if (items.length === 0) return false;

        const belongsToStorage = (deliveryItem: any, storageNumber: 1 | 2) => {
            const storage = String(deliveryItem?.product?.storage || deliveryItem?.material?.storage || "");
            return new RegExp(`kho\\s*${storageNumber}`, "i").test(storage);
        };
        const hasKho1 = items.some((deliveryItem: any) => belongsToStorage(deliveryItem, 1));
        const hasKho2 = items.some((deliveryItem: any) => belongsToStorage(deliveryItem, 2));
        const exportedKho1 = trip.is_first_storage_keeper_exported ?? trip.is_exported ?? false;
        const exportedKho2 = trip.is_second_storage_keeper_exported ?? trip.is_exported ?? false;

        if ((hasKho1 && !exportedKho1) || (hasKho2 && !exportedKho2)) return false;
        if (!hasKho1 && !hasKho2 && !trip.is_exported) return false;

        const returnedItems = items.filter((deliveryItem: any) => Number(deliveryItem.returned_quantity || 0) > 0);
        if (returnedItems.length === 0) return true;

        const hasReturnedKho1 = returnedItems.some((deliveryItem: any) => belongsToStorage(deliveryItem, 1));
        const hasReturnedKho2 = returnedItems.some((deliveryItem: any) => belongsToStorage(deliveryItem, 2));
        const importedKho1 = trip.is_first_storage_keeper_imported ?? trip.is_imported ?? false;
        const importedKho2 = trip.is_second_storage_keeper_imported ?? trip.is_imported ?? false;

        if ((hasReturnedKho1 && !importedKho1) || (hasReturnedKho2 && !importedKho2)) return false;
        return hasReturnedKho1 || hasReturnedKho2 ? true : Boolean(trip.is_imported);
    };


    const handleComplete = async (deliveryTripId: string | undefined) => {
        if (!deliveryTripId || updatingTripId) return;
        setUpdatingTripId(deliveryTripId);
        const requestBody = {
            status: DELIVERY_TRIP_STATUS.DELIVERED,
            arrived_at: dayjs().toISOString(),
        };
        try {
            await apiMethods.patch(`/product-flow/v2/delivery-trips/${deliveryTripId}/status/`, requestBody);
            success("THÀNH CÔNG", "Cập nhật chuyến vận chuyển thành công");
            handleRefresh();
        } catch (err) {
            error("Lỗi cập nhật chuyến vận chuyển", err);
        } finally {
            setUpdatingTripId(null);
        }
    };

    return (
        <TableRow>
            <StyledTableCell align="left">
                <Checkbox
                    checked={extraRowProps.isSelected}
                    onChange={(event) => extraRowProps.onSelect(item)}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                {item.vehicle_plate || item.car_plate}
            </StyledTableCell>
            <StyledTableCell align="left">{formatDate(item.scheduled_at || item.date) || "N/A"}</StyledTableCell>
            <StyledTableCell align="left">
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {item.product_orders.map((productOrder) => (
                        <div key={productOrder.id} style={{
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            padding: "16px",
                            backgroundColor: "#f9f9f9",
                        }}>
                            <StyledNavLink to={`/product-orders/${productOrder.id}`}
                                           style={{ fontWeight: "bold", fontSize: "1rem", color: "#1976d2" }}>
                                {productOrder.name}
                            </StyledNavLink>
                            <table style={{ width: "100%", marginTop: "8px", borderCollapse: "collapse" }}>
                                <thead>
                                <tr>
                                    <th style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                    }}>Sản phẩm
                                    </th>
                                    <th style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                    }}>Địa chỉ giao hàng
                                    </th>
                                    <th style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                    }}>Trạng thái
                                    </th>
                                    <th style={{
                                        textAlign: "left",
                                        padding: "8px",
                                        borderBottom: "1px solid #ddd",
                                    }}></th>
                                </tr>
                                </thead>
                                <tbody>
                                {productOrder?.delivery_trips?.map((trip) => (
                                    <tr key={trip.id} style={{ borderBottom: "1px solid #ddd" }}>
                                        <>
                                            <td style={{
                                                padding: "8px",
                                                width: "500px",
                                                overflowX: "auto",
                                                whiteSpace: "nowrap",
                                            }}>
                                                {((trip.delivery_products || []) || []).map((product, index) => (
                                                    <div key={index} style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        marginBottom: "4px",
                                                    }}>
                                                        <span
                                                            style={{ 
                                                                fontWeight: "bold",
                                                                flex: "1 1 auto",
                                                                minWidth: 0,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                            title={product?.product?.name || product?.material?.name || "N/A"}
                                                        >{product?.product?.name || product?.material?.name || "N/A"}</span>
                                                        <div
                                                            style={{
                                                                position: "relative",
                                                                display: "inline-block",
                                                                marginTop: "-4px",
                                                                marginRight: "8px",
                                                            }}
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    <TooltipContent>
                                                                        <TooltipCategory>
                                                                            <TooltipCircle
                                                                                backgroundColor={
                                                                                    deliveryTripBarChartColors[product?.product?.category ?? "Phuy"] || "#e0e0e0"
                                                                                }
                                                                            />
                                                                            <span>{product?.product?.category ?? "Phuy"}</span>
                                                                        </TooltipCategory>
                                                                    </TooltipContent>
                                                                }
                                                                placement="top"
                                                                componentsProps={{
                                                                    tooltip: {
                                                                        sx: {
                                                                            backgroundColor: "#ffffff",
                                                                            color: "#333",
                                                                            borderRadius: "8px",
                                                                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                                                            border: "1px solid #ddd",
                                                                            fontSize: "0.875rem",
                                                                            fontWeight: "500",
                                                                            lineHeight: "1.5",
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                <TooltipDot
                                                                    backgroundColor={
                                                                        deliveryTripBarChartColors[product?.product?.category ?? "Phuy"] || "#e0e0e0"
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                        <span
                                                            style={{
                                                                backgroundColor: "#e3f2fd",
                                                                color: "#1976d2",
                                                                borderRadius: "16px",
                                                                padding: "2px 8px",
                                                                fontSize: "0.75rem",
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            Giao: {product.quantity}
                                                        </span>
                                                        <span
                                                            style={{
                                                                backgroundColor: "#fce4ec",
                                                                color: "#c2185b",
                                                                borderRadius: "16px",
                                                                padding: "2px 8px",
                                                                fontSize: "0.75rem",
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            Trả lại: {product.returned_quantity || 0}
                                                        </span>
                                                    </div>
                                                ))}
                                            </td>
                                            <td style={{
                                                padding: "8px",
                                                minWidth: "220px",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}>
                                                {deliveryAddressLabel(trip.delivery_products || trip.items || [], trip.delivery_address || "") || (
                                                    <span className="tw-text-gray-600 tw-italic">
                                                        Chưa có địa chỉ
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: "8px" }}>
                                                <Status
                                                    colorTheme={getStatusColorTheme(trip.status)}
                                                    text={getStatusText(trip.status)}
                                                    width="124px"
                                                />
                                            </td>
                                            <td style={{ padding: "8px" }}>
                                                {canComplete && normalizeStatus(trip.status) === "IN_TRANSIT" && (
                                                    <LoadingButton
                                                        onClick={() => handleComplete(trip?.id)}
                                                        isLoading={updatingTripId === trip?.id}
                                                        disabled={updatingTripId !== null || !isTripReadyForCompletion(trip)}
                                                        text="Hoàn thành"
                                                        style={{
                                                            padding: "4px 12px",
                                                            fontSize: "0.875rem",
                                                            backgroundColor: isTripReadyForCompletion(trip) ? "#1976d2" : "#ccc",
                                                            color: "#fff",
                                                            textTransform: "none",
                                                        }}
                                                    />
                                                )}
                                            </td>
                                        </>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </StyledTableCell>
        </TableRow>
    );
};
